import { Link } from 'react-router-dom';
import { Button, Table } from 'react-bootstrap';
import { IconEdit, IconTrash, IconPackage } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import EmptyState from '../../../components/feedback/EmptyState';
import { formatDimensions, formatWeight } from '../../../shared/units';
import type { CaseListRow } from '../types';

interface CaseListTableProps {
  cases: CaseListRow[];
  onCreate: () => void;
  onEdit: (itemCase: CaseListRow) => void;
  onDelete: (itemCase: CaseListRow) => void;
}

const CaseListTable = ({ cases, onCreate, onEdit, onDelete }: CaseListTableProps) => {
  const { t } = useTranslation();

  if (cases.length === 0) {
    return (
      <EmptyState
        className="py-5"
        icon={<IconPackage size={40} className="app-empty-state__icon-glyph" aria-hidden="true" />}
        title={t('warehouses.cases.noCases')}
        message={t('warehouses.cases.emptyMessage', 'Create the first case to start managing grouped physical units and linked stock quantities.')}
        action={<Button onClick={onCreate}>{t('warehouses.cases.newCase')}</Button>}
      />
    );
  }

  return (
    <Table hover responsive className="table-vcenter card-table app-table mb-0">
      <thead>
        <tr>
          <th>{t('warehouses.cases.name')}</th>
          <th>{t('warehouses.cases.qrCode')}</th>
          <th>{t('warehouses.items.weight')}</th>
          <th>{t('warehouses.cases.outerDimensions')}</th>
          <th>{t('warehouses.cases.stocks')}</th>
          <th>{t('warehouses.quantityTotal')}</th>
          <th>{t('warehouses.quantityAvailable')}</th>
          <th>{t('warehouses.cases.entryCount')}</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        {cases.map((itemCase) => (
          <tr key={itemCase.id}>
            <td>
              <Link to={`/warehouses/cases/${itemCase.id}`} className="fw-semibold">
                {itemCase.name}
              </Link>
              {itemCase.description && <div className="text-muted small">{itemCase.description}</div>}
            </td>
            <td><code className="small">{itemCase.qr_code}</code></td>
            <td className="text-nowrap">{formatWeight(itemCase.weight, itemCase.weight_unit_symbol)}</td>
            <td className="text-nowrap">
              {formatDimensions(
                itemCase.outer_length,
                itemCase.outer_width,
                itemCase.outer_height,
                itemCase.outer_dimension_unit_symbol,
              )}
            </td>
            <td>
              {itemCase.stock_breakdown && itemCase.stock_breakdown.length > 0 ? (
                <div className="d-flex flex-wrap gap-1">
                  {itemCase.stock_breakdown.map((row) => (
                    <span
                      key={row.stock ?? 'none'}
                      className={`badge ${row.stock === null ? 'bg-secondary-lt' : 'bg-azure-lt'}`}
                    >
                      {row.stock === null ? t('warehouses.cases.noStock') : row.stock_name} ×{row.count}
                    </span>
                  ))}
                </div>
              ) : (
                <span className="text-muted">—</span>
              )}
            </td>
            <td>{itemCase.quantity_total}</td>
            <td>
              <span className={itemCase.quantity_available === 0 ? 'text-danger fw-semibold' : 'text-success fw-semibold'}>
                {itemCase.quantity_available}
              </span>
            </td>
            <td>{itemCase.entry_count}</td>
            <td className="text-end">
              <Button variant="ghost-secondary" size="sm" onClick={() => onEdit(itemCase)} className="me-1">
                <IconEdit size={16} />
              </Button>
              <Button variant="ghost-danger" size="sm" onClick={() => onDelete(itemCase)}>
                <IconTrash size={16} />
              </Button>
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
};

export default CaseListTable;
